import React, { useState, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { Alert, Row, Col } from "react-bootstrap";

import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieBanner from "./components/CookieBanner";

import HomeScreen from "./screens/HomeScreen";
import InStockScreen from "./screens/InStockScreen";
import CategoryScreen from "./screens/CategoryScreen";
import SearchResultsScreen from "./screens/SearchResultsScreen";
import ProductDetailScreen from "./screens/ProductDetailScreen";
import CartScreen from "./screens/CartScreen";
import LoginScreen from "./screens/LoginScreen";
import RegisterScreen from "./screens/RegisterScreen";
import UpdateClientScreen from "./screens/UpdateClientScreen";
import PreviousPurchasesScreen from "./screens/PreviousPurchasesScreen";
import PasswordForgot from "./screens/PasswordForgotScreen";
import ResetPassword from "./screens/ResetPasswordScreen";
import LogoutScreen from "./screens/LogoutScreen";
import PrivacyPolicyScreen from "./screens/PrivacyPolicyScreen";
import ImpressumScreen from "./screens/ImpressumScreen";

import ReturnRefundPolicyScreen from "./screens/ReturnRefundPolicyScreen";
import CookiesConsentScreen from "./screens/CookiesConsentScreen";
import ContactFormScreen from "./screens/ContactFormScreen";

import { CartProvider } from "./contexts/CartContext";
import { ClientNameContext } from "./contexts/clientNameContext";

import { API_BASE_URL } from "./Config";
import "./i18n";

import { PayPalScriptProvider } from "@paypal/react-paypal-js";

import KlarnaConfirmationScreen from "./screens/KlarnaConfirmationScreen";
import KlarnaCheckoutScreen from "./screens/KlarnaCheckoutScreen";
import KlarnaTermsScreen from "./screens/KlarnaTermsScreen";


function App() {
  const location = useLocation();

  // 🔍 Analytics
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
      console.log(`Página rastreada: ${location.pathname}`);
    } else {
      console.warn("gtag ainda não está disponível");
    }
  }, [location]);

  // 💳 PayPal
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [isPayPalReady, setIsPayPalReady] = useState(false);

  const initialOptions = {
    "client-id": paypalClientId,
    currency: "EUR",
    intent: "capture",
  };

  useEffect(() => {
    const fetchClientId = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/paypal-client-id/`);
        const data = await response.json();
        setPaypalClientId(data.paypalClientId);
      } catch (error) {
        console.error("Erro ao buscar PayPal Client ID:", error);
      } finally {
        setIsPayPalReady(true);
      }
    };
    fetchClientId();
  }, []);

  // 👤 Cliente
  const [clientName, setClientName] = useState("");
  const [alertMessage, setAlertMessage] = useState(null);

  const handleAlert = (message) => {
    setAlertMessage(message);
    setTimeout(() => setAlertMessage(null), 3000);
  };

  return (
    <ClientNameContext.Provider value={{ clientName, setClientName }}>
      <ScrollToTop />
      <CartProvider>
        <Header />
        <main className="pb-5">
          {alertMessage && (
            <Row>
              <Col sm={12}>
                <Alert variant="warning">{alertMessage}</Alert>
              </Col>
            </Row>
          )}

          <Routes>
            <Route path="/" exact element={<HomeScreen handleAlert={handleAlert} />} />
            <Route path="/instock" element={<InStockScreen />} />
            <Route path="/product/:slug" element={<ProductDetailScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            <Route path="/update-client" element={<UpdateClientScreen />} />
            <Route path="/previous-purchases" element={<PreviousPurchasesScreen />} />
            <Route path="/password_forgot" element={<PasswordForgot />} />
            <Route path="/reset_password/:token" element={<ResetPassword />} />
            <Route path="/logout" element={<LogoutScreen />} />
            <Route path="/search" element={<SearchResultsScreen />} />
            <Route path="/search/:searchTerm" element={<SearchResultsScreen />} />
            <Route path="/category/:categoryName" element={<CategoryScreen />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
            <Route path="/impressum" element={<ImpressumScreen />} />
           
            <Route path="/return-refund" element={<ReturnRefundPolicyScreen />} />
            <Route path="/cookies-consent" element={<CookiesConsentScreen />} />
            <Route path="/contact" element={<ContactFormScreen />} />

            <Route path="/klarna/terms" element={<KlarnaTermsScreen />} />
            <Route path="/klarna/checkout/:orderId" element={<KlarnaCheckoutScreen />} />
            <Route path="/klarna/confirmation/:orderId" element={<KlarnaConfirmationScreen />} />


            <Route
              path="/cart"
              element={
                isPayPalReady ? (
                  paypalClientId ? (
                    <PayPalScriptProvider options={initialOptions}>
                      <CartScreen />
                    </PayPalScriptProvider>
                  ) : (
                    <CartScreen />
                  )
                ) : (
                  <div className="text-center pt-5">Carregando PayPal...</div>
                )
              }
            />
          </Routes>
        </main>
        <CookieBanner />
        <WhatsAppButton />
        <Footer />
      </CartProvider>
    </ClientNameContext.Provider>
  );
}

export default App;
